'use client'

import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Skin } from '@/ui/skin/Skin'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { ProductRowLinkedProductsDocument } from '@gql-generated/graphql'
import { atom, useAtom } from 'jotai'
import { useCallback } from 'react'
import { ProductRowLinkedProductsDrawer } from './_components/ProductRowLinkedProductsDrawer.client'

// Atom to manage the open state of the linked products drawer across re-renders
const openLinkedProductsDrawerReferenceAtom = atom<string | undefined>(undefined)

gql(/* GraphQL */ `
  fragment ProductRowLinkedProducts_Product on Product {
    reference
    canAddToCart
    linkedProducts(includeBundleInLinkedProducts: true) {
      reference
      ...ProductCalculatedPrice
      bestArticlePrice
      price {
        exclTax
        discountExclTax
        discountPercentage
        tag
      }
      name
      type
      mainImage {
        ...FullPicture
      }
      brand {
        name
        urlKey
      }
      stock {
        availability
        maxQuantity
        isPriceAvailable
        isSellable
        packaging {
          ...FullPackaging
        }
      }
      urlKey
      canAddToCart
      clickAndCollectStatus
    }
  }

  query ProductRowLinkedProducts($reference: String!) {
    productByReference(reference: $reference) {
      ...ProductRowLinkedProducts_Product
    }
  }
`)

type ProductRowLinkedProductsProps = {
  reference: string
  addProductToCartAction: (product: {
    sku: string
    quantity: number
    price: number
  }) => Promise<void>
  className?: string
}

export function ProductRowLinkedProducts({
  reference,
  addProductToCartAction,
  className,
}: ProductRowLinkedProductsProps) {
  const { t } = useTranslation(['productRowLinkedProducts'])
  const [openDrawerReference, setOpenDrawerReference] = useAtom(
    openLinkedProductsDrawerReferenceAtom,
  )

  const isDrawerOpen = openDrawerReference === reference
  const { data: linkedProducts } = useQuery(ProductRowLinkedProductsDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: { reference },
    skip: !isDrawerOpen || isSSR,
  })

  const handleOpenDrawer = useCallback(() => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'page panier',
      eventAction: 'plot produit',
      eventLabel: 'cta produits complémentaires',
    })
    setOpenDrawerReference(reference)
  }, [setOpenDrawerReference, reference])

  const handleCloseDrawer = useCallback(() => {
    setOpenDrawerReference(undefined)
  }, [setOpenDrawerReference])

  return (
    <>
      {/* TODO: Replace with feature component */}
      {/* FIXME: Remove the `embed:hidden` class from the Skin component when this feature is ready in WebView */}
      <Skin
        className={twMerge(
          className,
          'flex flex-wrap items-center justify-center gap-0.5 rounded-sm px-2 py-1 embed:hidden',
        )}
        tint="brand-1-light"
      >
        <span className="font-bold text-info-fg">{t('productRowLinkedProducts:text')}</span>
        <Button size="md" variant="link" iconRight={<Icon.ArrowRight />} onPress={handleOpenDrawer}>
          {t('productRowLinkedProducts:link')}
        </Button>
      </Skin>

      {isDrawerOpen && (
        <ProductRowLinkedProductsDrawer
          product={linkedProducts?.productByReference}
          isDrawerOpen={isDrawerOpen}
          handleCloseDrawer={handleCloseDrawer}
          addProductToCartAction={addProductToCartAction}
        />
      )}
    </>
  )
}
